import {
	PINDROP_CUP,
	type PindropInput,
	type PindropState,
	pindropCups,
	pindropWon,
} from "@playpal/core";
import { useEffect } from "react";

/** The same five colours as the tubes, so the two games look related. */
const COLOURS = [
	{ name: "red", fill: "bg-red-500" },
	{ name: "blue", fill: "bg-blue-500" },
	{ name: "green", fill: "bg-green-500" },
	{ name: "yellow", fill: "bg-yellow-400" },
	{ name: "purple", fill: "bg-purple-500" },
] as const;

const nameOf = (ball: number) => COLOURS[ball - 1]?.name ?? "?";
const fillOf = (ball: number) => COLOURS[ball - 1]?.fill ?? "bg-slate-400";

export const PINDROP_ASPECT = 1;

interface Props {
	state: PindropState;
	onPress: (input: PindropInput) => void;
	live: boolean;
}

/**
 * The pins above, the tray below.
 *
 * Still between one pull and the next, like the tubes, so every pin says what
 * it is holding and the tray says what has landed. This game is entirely about
 * order, and order is the one thing words are good at.
 */
export function PindropField({ state, onPress, live }: Props) {
	const cups = pindropCups(state.level);

	return (
		<div className="flex h-full w-full flex-col justify-between gap-3 p-2">
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
				{state.pins.map((balls, index) => (
					<Pin
						// A fixed set for the level, so its place in it identifies it.
						key={index}
						balls={balls}
						index={index}
						gone={state.pulled[index]}
						onPress={() => onPress({ pull: index })}
						live={live}
					/>
				))}
			</div>

			<Tray tray={state.tray} cups={cups} />
		</div>
	);
}

function Pin({
	balls,
	index,
	gone,
	onPress,
	live,
}: {
	balls: readonly number[];
	index: number;
	gone: boolean;
	onPress: () => void;
	live: boolean;
}) {
	const contents = balls.map(nameOf).join(", ");

	return (
		<button
			type="button"
			disabled={!live || gone}
			aria-label={gone ? `Pin ${index + 1}: pulled` : `Pin ${index + 1}: ${contents}`}
			onPointerDown={(event) => {
				event.preventDefault();
				if (live && !gone) onPress();
			}}
			className={`flex items-center gap-1 rounded-2xl border-2 p-1.5 transition ${
				gone
					? "border-dashed border-white/20 opacity-30"
					: "border-white/40 bg-black/25 active:scale-95"
			}`}
		>
			<span aria-hidden="true" className="text-lg leading-none">
				📌
			</span>
			<span className="flex flex-1 gap-1" aria-hidden="true">
				{balls.map((ball, at) => (
					<span
						key={at}
						className={`h-5 flex-1 rounded-full ${gone ? "bg-white/10" : fillOf(ball)}`}
					/>
				))}
			</span>
		</button>
	);
}

/**
 * What has landed, in cups of four.
 *
 * The cup a ball falls into is decided by how many landed before it, which is
 * the whole game — so the divisions have to be plain, and a finished cup has to
 * look finished.
 */
function Tray({ tray, cups }: { tray: readonly number[]; cups: number }) {
	const won = pindropWon(tray);

	return (
		<div>
			<div className="flex gap-2">
				{Array.from({ length: cups }, (_, cup) => {
					const balls = tray.slice(cup * PINDROP_CUP, (cup + 1) * PINDROP_CUP);
					const full = balls.length === PINDROP_CUP;
					const pure = full && balls.every((ball) => ball === balls[0]);
					return (
						<ul
							key={cup}
							// A list, because that is what a cup of balls is — and a bare
							// div takes no `aria-label` at all, which is the one thing about
							// this game worth saying out loud.
							aria-label={`Cup ${cup + 1}: ${balls.length ? balls.map(nameOf).join(", ") : "empty"}`}
							className={`flex flex-1 gap-1 rounded-b-2xl rounded-t-md border-4 p-1 ${
								pure ? "border-lime-300 bg-lime-300/20" : "border-white/30 bg-black/25"
							}`}
						>
							{Array.from({ length: PINDROP_CUP }, (_, slot) => (
								<li
									key={slot}
									aria-hidden="true"
									className={`h-8 flex-1 rounded-full ${
										balls[slot] ? fillOf(balls[slot]) : "bg-white/10"
									}`}
								/>
							))}
						</ul>
					);
				})}
			</div>
			<p role="status" className="mt-1 text-center text-sm font-semibold opacity-70">
				{won} of {cups} cups sorted
			</p>
		</div>
	);
}

/** Starting the level again, and the number keys for a laptop. */
export function PindropControls({
	state,
	onPress,
	live,
}: {
	state: PindropState;
	onPress: (input: PindropInput) => void;
	live: boolean;
}) {
	useNumberKeys(state.pins.length, onPress, live);

	return (
		<div className="flex items-center justify-center gap-4 py-2">
			<p className="text-base font-semibold opacity-70">Level {state.level + 1}</p>
			<button
				type="button"
				onPointerDown={(event) => {
					event.preventDefault();
					if (live) onPress({ restart: true });
				}}
				className="rounded-2xl bg-white/15 px-4 py-2 text-base font-bold shadow transition active:scale-95"
			>
				↩︎ Put the pins back
			</button>
		</div>
	);
}

function useNumberKeys(pins: number, onPress: (input: PindropInput) => void, live: boolean) {
	useEffect(() => {
		if (!live) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "r") {
				event.preventDefault();
				onPress({ restart: true });
				return;
			}
			const pin = Number.parseInt(event.key, 10);
			if (!Number.isInteger(pin) || pin < 1 || pin > pins) return;
			event.preventDefault();
			onPress({ pull: pin - 1 });
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [pins, onPress, live]);
}
